import { EditContactsPage } from './EditContacts.styled';
import { selectCustomStyles } from '../../common/shared/select.styled';
import Select from 'react-select';
import { ContactCardStyled } from '../reports/tenantList/TenantList.styled';
import { Scrollbar } from 'react-scrollbars-custom';
import { useEffect, useMemo, useState } from 'react';
import Pen from '../../images/managers/pen.png';
import CreateContactModal from './CreateContactModal';
import EditContactModal from './EditContactModal';
import { contactOwner, selectContactOwnerOptions } from './common';
import { useAccessToken } from '../../common/hooks/useAccessToken';
import { NotificationManager } from 'react-notifications';
import ContentLoader from '../../common/simple/ContentLoader';
import { useSelector } from 'react-redux';
import { isAdminSelector } from '../../store/auth/selectors';

const EditContacts = () => {
    const [contactOwnerOption, setContactOwnerOption] = useState(selectContactOwnerOptions[0]);
    const [selectedCardEntityId, setSelectedCardEntityId] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);
    const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
    const [typeToEdit, setTypeToEdit] = useState(null);
    const [valueToEdit, setValueToEdit] = useState(null);
    const [idOfEdit, setIdOfEdit] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const client = useAccessToken();
    const isLtrEnabled = useSelector(state => state.common.ltrEnabled);
    const isAdmin = useSelector(isAdminSelector);

    const reloadContacts = async () => {
        setIsLoading(true);
        try {
            setSelectedCardEntityId(null);
            const response = await client.managers.contacts.getContacts();
            setContacts(response);
        } catch (e) {
            NotificationManager.error(e.message);
            throw e;
        } finally {
            setIsLoading(false);
        }

    }

    useEffect(() => {
        if (!client) return;
        reloadContacts();
    }, [client]);

    const contactsByBuildings = useMemo(() => {
        const obj = {};
        contacts.forEach(contact => {
            if (contact.entityType === 'buildingID') {
                if (!obj[contact.entityId]) {
                    obj[contact.entityId] = {
                        entityId: contact.entityId,
                        entityType: 'buildingID',
                        label: 'building',
                        contacts: []
                    };
                }
                obj[contact.entityId].contacts.push(contact);
            }
        });
        return obj;
    }, [contacts]);

    const contactsByPartners = useMemo(() => {
        const obj = {};
        contacts.forEach(contact => {
            if (contact.entityType === 'partnerID') {
                if (!obj[contact.entityId]) {
                    obj[contact.entityId] = {
                        entityId: contact.entityId,
                        entityType: 'partnerID',
                        label: 'partner',
                        contacts: []
                    };
                }
                obj[contact.entityId].contacts.push(contact);
            }
        });
        return obj;
    }, [contacts]);

    const renderCards = () => {
        let entitiesToRender = [];
        if (contactOwnerOption.value === contactOwner.buildingID) {
            entitiesToRender = Object.values(contactsByBuildings);
        } else if (contactOwnerOption.value === contactOwner.partnerID) {
            entitiesToRender = Object.values(contactsByPartners);
        }

        return entitiesToRender.map(info => {
            const isSelected = info.entityId === selectedCardEntityId && info.entityType === contactOwnerOption.value;

            return (
                <ContactCardStyled
                    selected={isSelected}
                    onClick={() => setSelectedCardEntityId(info.entityId)}
                    ltr={isLtrEnabled}
                    cursor="pointer"
                    key={info.entityId}
                >
                    {info.label}: {info.entityId}
                </ContactCardStyled>
            )
        })
    }

    const renderContacts = () => {
        let contactsToRender = [];
        if (contactOwnerOption.value === contactOwner.buildingID) {
            contactsToRender = contactsByBuildings[selectedCardEntityId]?.contacts || [];
        } else if (contactOwnerOption.value === contactOwner.partnerID) {
            contactsToRender = contactsByPartners[selectedCardEntityId]?.contacts || [];
        }

        if (contactsToRender.length === 0) {
            return 'Select option from list';
        }

        return contactsToRender.sort((a, b) => a.type - b.type).map(contact => {
            return (
                <div key={contact.id} className="contact-line">
                    <div>
                        <div className="type">{contact.type}</div>
                        <div className="value">{contact.value}</div>
                    </div>
                    <div className="actions">
                        <div>
                            <img
                                onClick={() => {
                                    setIsEditContactModalOpen(true);
                                    setTypeToEdit(contact.type);
                                    setValueToEdit(contact.value);
                                    setIdOfEdit(contact.id);
                                }}
                                src={Pen}
                                alt="pen"
                            />
                        </div>
                    </div>
                </div>
            )
        })
    }

    if (!isAdmin) {
        return null;
    }

    return (
        <EditContactsPage>
            <h1 className="page-title">
                Edit contacts
            </h1>
            <div className="container">
                <div className="row">
                    <div className="col-md-12">
                        <button
                            className="bllink-button create-new"
                            onClick={() => setIsCreateContactModalOpen(true)}
                        >Create new</button>
                        <button
                            className="bllink-button edit-partner-name"
                            onClick={() => window.open('https://bllink.retool.com/apps/1ce3a886-8232-11ed-ba11-cfd96a040b68/partners/Edit%20partner%20name', '_blank')}
                        >Edit partner name</button>
                    </div>
                    <div className="col-md-6">
                        <div className="cards">
                            <h2>Contacts list</h2>
                            <Select
                                styles={selectCustomStyles}
                                options={selectContactOwnerOptions}
                                placeholder="Select contact owner type"
                                value={contactOwnerOption}
                                onChange={setContactOwnerOption}
                            />
                            <Scrollbar
                                style={{ width: '100%', height: 'calc(100vh - 100px)', marginTop: 20 }}
                                trackYProps={{
                                    renderer: props => {
                                        const { elementRef, ...restProps } = props;
                                        return <div {...restProps} ref={elementRef} className="scroll-track" />;
                                    }
                                }}
                                thumbYProps={{
                                    renderer: props => {
                                        const { elementRef, ...restProps } = props;
                                        return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                                    }
                                }}
                                rtl={!isLtrEnabled}
                                noScrollX
                            >
                                <div className="cards-wrapper">
                                    {isLoading ? <ContentLoader width="100%" height={200} /> : renderCards()}
                                </div>
                            </Scrollbar>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="info">
                            <div className="info-card-wrapper">
                                <ContactCardStyled ltr={isLtrEnabled}>
                                    {renderContacts()}
                                </ContactCardStyled>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CreateContactModal
                show={isCreateContactModalOpen}
                onHide={() => setIsCreateContactModalOpen(false)}
                reloadContacts={reloadContacts}
            />
            <EditContactModal
                show={isEditContactModalOpen}
                onHide={() => setIsEditContactModalOpen(false)}
                type={typeToEdit}
                value={valueToEdit}
                id={idOfEdit}
                reloadContacts={reloadContacts}
            />
        </EditContactsPage>
    )
}

export default EditContacts;